import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    space, colors, fontSizes, lineHeights, fontWeights, radii
} from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Grid, Text, Divider, Image, Button, Flex, Link
} from 'components/ui';
import Modal from 'components/Modal/Modal';
import Loader from 'components/Loader/Loader';
import SubscriptionInfoSection from 'components/GlobalModals/ManageSubscriptionModal/components/SubscriptionInfoSection';
import SubscriptionProduct from 'components/GlobalModals/ManageSubscriptionModal/components/SubscriptionProduct';
import { useManageSubscriptionModal } from 'components/GlobalModals/ManageSubscriptionModal/hooks/useManageSubscriptionModal';

const AUTO_DISMISS_DELAY = 2000; // 2 seconds

/**
 * ManageSubscriptionModalRedesign - Accessible modal for managing auto-replenishment subscriptions
 *
 * Allows users to view and modify their subscription details including:
 * - Skip or pause options
 * - Delivery frequency
 * - Shipping address
 * - Payment method
 *
 * @component
 */
function ManageSubscriptionModalRedesign({
    onResume,
    currentSubscription,
    fireGenericErrorAnalytics,
    isOpen,
    isResumeSuccessful,
    onChangeDate,
    onChangeFrequency,
    onChangePayment,
    onChangeShipping,
    onDismiss,
    onPauseSubscription,
    onSkipDelivery,
    onSkuSwapClick,
    onUnsubscribeClick
}) {
    const {
        creditCard, featureFlags, isLoading, localization, onModalClose, product, savingsCopy, shippingInfo, shouldShowItemVariation
    } =
        useManageSubscriptionModal({
            currentSubscription,
            onDismiss,
            onResume,
            fireGenericErrorAnalytics
        });

    const handleResume = useCallback(() => {
        if (isResumeSuccessful) {
            return;
        }

        onResume?.();
    }, [onResume, isResumeSuccessful]);

    /**
     * Auto-dismiss modal after 2 seconds when resume is successful
     */
    useEffect(() => {
        if (!isResumeSuccessful) {
            return undefined;
        }

        const timeoutId = setTimeout(() => onModalClose(), AUTO_DISMISS_DELAY);

        return () => clearTimeout(timeoutId);
    }, [isResumeSuccessful, onModalClose]);

    return (
        <Modal
            width={0}
            onDismiss={onModalClose}
            isOpen={isOpen}
        >
            <Modal.Header>
                <Modal.Title children={onResume ? localization.resumeModalTitle : localization.manageSubscription} />
            </Modal.Header>
            <Modal.Body
                paddingTop={space[3]}
                paddingBottom={space[4]}
                paddingX={space[4]}
                css={styles.modalBody}
            >
                {isLoading && <Loader isShown={true} />}
                {!isLoading && product && (
                    <>
                        <SubscriptionProduct
                            isSkuSwapEnabled={featureFlags.enableReplenishmentSkuSwap}
                            localization={localization}
                            onSkuSwapClick={onSkuSwapClick}
                            product={product}
                            shouldShowItemVariation={shouldShowItemVariation}
                        />
                        <Flex css={styles.contentContainer}>
                            <Flex css={styles.operations}>
                                <Text
                                    is='h4'
                                    css={styles.nextShipment}
                                >
                                    {localization.nextShipment} <Text css={styles.nextShipmentDate}>{shippingInfo.nextShipmentDate}</Text>
                                </Text>

                                {!!(featureFlags.enableReplenishmentDateModifiable && onChangeDate) && (
                                    <Button
                                        variant='primary'
                                        children={localization.changeDate}
                                        onClick={onChangeDate}
                                    />
                                )}
                                {!!(featureFlags.enableReplenishmentOperations && onSkipDelivery && onPauseSubscription) && (
                                    <Flex gap={space[3]}>
                                        <Button
                                            size='sm'
                                            variant='secondary'
                                            css={styles.operationButton}
                                            onClick={onSkipDelivery}
                                            children={localization.skip}
                                        />
                                        <Button
                                            size='sm'
                                            variant='secondary'
                                            css={styles.operationButton}
                                            onClick={onPauseSubscription}
                                            children={localization.pause}
                                        />
                                    </Flex>
                                )}
                            </Flex>

                            <Divider />

                            <SubscriptionInfoSection
                                isInteractive={featureFlags.enableReplenishmentFrequencyModifiable}
                                onClick={onChangeFrequency}
                                title={localization.deliveryTitle}
                            >
                                <Text css={styles.baseFont}>{localization.deliveryValue}</Text>
                            </SubscriptionInfoSection>

                            <Divider />

                            <SubscriptionInfoSection
                                isInteractive={featureFlags.enableReplenishmentAddressModifiable}
                                onClick={onChangeShipping}
                                title={localization.shippingTitle}
                            >
                                {shippingInfo.hasData ? (
                                    <>
                                        <Text
                                            is='p'
                                            fontWeight={fontWeights.bold}
                                        >
                                            {shippingInfo.fullName}
                                        </Text>
                                        <Text is='p'>
                                            {shippingInfo.addressLine1}
                                            <br />
                                            {shippingInfo.fullAddress}
                                            <br />
                                            {shippingInfo.phoneNumber}
                                        </Text>
                                    </>
                                ) : (
                                    featureFlags.enableReplenishmentAddressModifiable && <Text>{localization.selectAddress}</Text>
                                )}
                            </SubscriptionInfoSection>

                            <Divider />

                            <SubscriptionInfoSection
                                isInteractive={featureFlags.enableReplenishmentPaymentModifiable}
                                onClick={onChangePayment}
                                title={localization.paymentTitle}
                            >
                                {creditCard.hasData ? (
                                    <Flex gap={space[3]}>
                                        <Image
                                            height={32}
                                            width={50}
                                            src={creditCard.logo}
                                            alt={creditCard.alt}
                                        />
                                        <Text is='p'>
                                            {creditCard.label}
                                            <br />
                                            <span css={styles.smFont}>
                                                {localization.creditCardExpDate} {creditCard.expDate}
                                            </span>
                                        </Text>
                                    </Flex>
                                ) : (
                                    featureFlags.enableReplenishmentPaymentModifiable && <Text>{localization.selectPayment}</Text>
                                )}
                            </SubscriptionInfoSection>

                            <Text
                                is='p'
                                css={[styles.legalCopy, styles.smFont]}
                            >
                                {localization.legalCopy}
                            </Text>

                            {onResume ? (
                                <Text
                                    is='p'
                                    css={styles.annuallySaving}
                                    dangerouslySetInnerHTML={{
                                        __html: product.acceleratedPromotion ? savingsCopy.hotDealSaving : savingsCopy.annuallySaving
                                    }}
                                />
                            ) : (
                                <>
                                    <Divider />

                                    <Link
                                        css={[styles.cancelSubscription, styles.linkColor]}
                                        onClick={onUnsubscribeClick}
                                    >
                                        {localization.cancelSubscription}
                                    </Link>
                                </>
                            )}
                        </Flex>
                    </>
                )}
            </Modal.Body>
            {onResume && (
                <Modal.Footer
                    hasShadowBorderForSMUI={true}
                    paddingX={4}
                >
                    <Grid css={styles.footerActions}>
                        <Button
                            variant='secondary'
                            onClick={onModalClose}
                        >
                            {localization.cancel}
                        </Button>
                        <Button
                            variant='primary'
                            onClick={handleResume}
                        >
                            {isResumeSuccessful ? (
                                <>
                                    <Image
                                        src='/img/ufe/success-login.svg'
                                        width={20}
                                        height={20}
                                        disableLazyLoad={true}
                                        css={styles.submitButtonIcon}
                                    />
                                    {localization.resumeSuccessButtonText}
                                </>
                            ) : (
                                localization.resumeSubmitButtonText
                            )}
                        </Button>
                    </Grid>
                </Modal.Footer>
            )}
        </Modal>
    );
}

const styles = {
    modalBody: {
        lineHeight: lineHeights.tight,
        minHeight: '400px'
    },

    // Operations Section (ChangeDate/Skip/Pause buttons)
    nextShipment: {
        fontWeight: fontWeights.bold
    },
    nextShipmentDate: {
        color: colors.green
    },
    operations: {
        flexDirection: 'column',
        gap: space[2]
    },
    operationButton: {
        flexGrow: 1
    },

    // Tab Section (Delivery, Shipping, Payment)
    contentContainer: {
        flexDirection: 'column',
        gap: space[4]
    },

    // Footer
    annuallySaving: {
        background: colors.lightGray,
        padding: `${space[2]}px ${space[3]}px`,
        borderRadius: radii[2]
    },
    footerActions: {
        gap: space[4],
        gridTemplateColumns: '1fr 1fr'
    },
    legalCopy: {
        marginTop: -space[2]
    },
    cancelSubscription: {
        marginRight: 'auto'
    },
    submitButtonIcon: {
        marginRight: space[2]
    },

    // Shared/Utility
    smFont: {
        fontSize: fontSizes.sm
    },
    baseFont: {
        fontSize: fontSizes.base
    },
    linkColor: {
        color: colors.blue
    }
};

ManageSubscriptionModalRedesign.propTypes = {
    currentSubscription: PropTypes.shape({
        frequency: PropTypes.number,
        frequencyType: PropTypes.string,
        items: PropTypes.array,
        paymentId: PropTypes.string,
        profileId: PropTypes.string,
        shippingAddressId: PropTypes.string
    }).isRequired,
    fireGenericErrorAnalytics: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    isResumeSuccessful: PropTypes.bool,
    onChangeDate: PropTypes.func,
    onChangeFrequency: PropTypes.func.isRequired,
    onChangePayment: PropTypes.func.isRequired,
    onChangeShipping: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onPauseSubscription: PropTypes.func,
    onResume: PropTypes.func,
    onSkipDelivery: PropTypes.func,
    onSkuSwapClick: PropTypes.func,
    onUnsubscribeClick: PropTypes.func
};

export default wrapFunctionalComponent(ManageSubscriptionModalRedesign, 'ManageSubscriptionModalRedesign');
